import { useState, useEffect } from "react";
import "./style.css";
import { ReactComponent as Settings } from "../../Assets/icon/settings.svg";
import { ReactComponent as Right } from "../../Assets/icon/chevron-right.svg";
import arrowLeft from "../../Assets/icon/arrow-narrow-left.svg";
import {
  getNotification,
  markAsReadNotification,
  markReadV2,
} from "../../Services/notificationAris";
import ContentLoader from "../../Pages/LoadingPage/ContentLoader";
import reminder_icon from "../../Assets/icon/notification_reminder.svg";
import portrait_icon from "../../Assets/icon/notification_portrait.svg";
import coach_icon from "../../Assets/icon/notification_coach.svg";
import "../../Pages/TermsofUse/style.css";
import rehypeRaw from "rehype-raw";
import ReactMarkdown from "react-markdown";
import InviteDecisionModal from "../InviteDecisionModal";

function Notify({ notification, onRouted, refreshUnreadCount, onMarked, onOpenInvite }) {
  const getNotificationIcon = (type) => {
    switch (type) {
      case "coach":
        return coach_icon;
      case "reminder":
        return reminder_icon;
      default:
        return portrait_icon;
    }
  };

  const getRelativeTime = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInMillis = now - past;

    const minutes = Math.floor(diffInMillis / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} days ago`;
    if (hours > 0) return `${hours} hours ago`;
    return `${minutes} mins ago`;
  };

  const isRoutable = () => {
    const subtype = notification?.notification_subtype;
    return [
      "relationship_request",
      "element_created",
      "portrait_created",
    ].includes(subtype);
  };

  const markRead = async () => {
    try {
      // Prefer v2 endpoint
      await markReadV2({ notification_id: notification?._id, mark_all: false });
    } catch (_) {
      try { await markAsReadNotification(notification?._id); } catch (__) { }
    }
    if (typeof onMarked === "function") onMarked(notification?._id);
    if (typeof refreshUnreadCount === "function") {
      try { await refreshUnreadCount(); } catch (_) { }
    }
  };

  const handleClickToRoute = async (e) => {
    if (!isRoutable()) {
      // Non-actionable: just mark as read, keep dialog open
      if (!notification?.is_read) await markRead();
      return;
    }
    // Actionable: mark, then either open dialog or route
    if (!notification?.is_read) await markRead();
    switch (notification?.notification_subtype) {
      case "relationship_request": {
        // Open the modal and keep the notification container mounted
        if (typeof onOpenInvite === "function") {
          onOpenInvite(notification);
        }
        break;
      }
      case "element_created":
        onRouted();
        window.location.href = "/elements";
        break;
      case "portrait_created":
        onRouted();
        window.location.href = "/elements";
        break;
      default:
        break;
    }
  };

  const unread = !notification?.is_read;
  const actionable = isRoutable();
  const containerClass = `notify-container-hero ${unread ? (actionable ? 'actionable-unread' : 'nonaction-unread') : 'read'}`;

  return (
    <div className={containerClass} onClick={handleClickToRoute}>
      <div className="notify-left">
        <img
          src={getNotificationIcon(notification.notification_type)}
          alt="icon"
        />
      </div>
      <div className="notify-mid">
        <div className="notify-cnt-time">
          {notification.notification_type === "invite" &&
            getRelativeTime(notification.created_at)}
        </div>
        <div className="notify-cnt-head">{notification.content.title}</div>
        <div className="notify-cnt-sub">
          {/* {notification?.content?.template
            ?.replaceAll("\\n", "\n") // Convert literal \\n to real \n
            ?.replaceAll("\\t", "\t") // Convert literal \\t to real \t
            ?.split(/\n/g) // Split by actual \n
            ?.map((line, index) => (
              <span key={index} id={`line-${index}`}>
                {line?.split(/\t/g).map((segment, i) => (
                  <span key={i}>
                    {segment}
                    {i !== line.split(/\t/g).length - 1 && (
                      <>&nbsp;&nbsp;&nbsp;&nbsp;</>
                    )}
                  </span>
                ))}
                <br />
              </span>
            ))} */}
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: "h3",
              h2: ({ node, ...props }) => (
                <h4 style={{ marginBottom: '0px' }} {...props} />
              ),
              p: ({ node, ...props }) => (
                <p style={{ marginBottom: '0px' }} {...props} />
              ),
              sup: ({ node, ...props }) => (
                <sup style={{ fontSize: '0.6em', verticalAlign: 'super' }} {...props} />
              ),
            }}
          >
            {notification?.content?.template || ""}
          </ReactMarkdown>
        </div>
        <div className="notify-cnt-message">
          {notification?.content?.additional_content?.message}
        </div>
      </div>
      {actionable ? (
        // Actionable: show chevron; color handled via CSS based on unread/read
        <div className="notify-right notify-right-chevron">
          <Right />
        </div>
      ) : (
        // Non-actionable: show blue dot if unread; nothing if read
        !notification?.is_read ? (
          <div className="notify-right-dot" />
        ) : (
          <div style={{ width: 12 }} />
        )
      )}
    </div>
  );
}

function NotificationContainer({
  showNotification,
  setshowNotification,
  onRouted,
  refreshUnreadCount,
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteNotification, setInviteNotification] = useState(null);

  const fetchNotifications = async (page) => {
    setLoading(true);
    try {
      const response = await getNotification({ page });
      if (response && response.content && response.content.length > 0) {
        setNotifications((prev) => [...prev, ...response.content]);
        setHasMore(page < response.max_page_count);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !loading && hasMore) {
      setCurrentPage((prev) => prev + 1);
      fetchNotifications(currentPage + 1);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setNotifications([]);
    fetchNotifications(1);
  }, []);

  const renderNotifications = () => {
    if (!notifications?.length && !loading) {
      return (
        <div className="no-notifications">
          No notifications for now. You’re all set!
        </div>
      );
    }
    return notifications.map((notification) => (
      <Notify
        key={notification._id}
        notification={notification}
        onRouted={onRouted}
        refreshUnreadCount={refreshUnreadCount}
        onMarked={(id) => {
          setNotifications((prev) => prev.map((n) => n._id === id ? { ...n, is_read: true } : n));
        }}
        onOpenInvite={(notif) => { setInviteNotification(notif); setInviteModalOpen(true); }}
      />
    ));
  };

  const handleMarkAllRead = async () => {
    try {
      await markReadV2({ mark_all: true });
      // Optimistically mark all as read locally
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      // Refresh bell count
      if (typeof refreshUnreadCount === "function") {
        await refreshUnreadCount();
      }
      // Optionally, refetch first page to ensure sync
      setCurrentPage(1);
      setHasMore(true);
      setNotifications([]);
      await fetchNotifications(1);
      if (typeof refreshUnreadCount === "function") {
        await refreshUnreadCount();
      }
    } catch (e) {
      // Silently fail; optionally add toast here
      if (typeof refreshUnreadCount === "function") {
        try { await refreshUnreadCount(); } catch (_) { }
      }
    }
  };


  const handleBackClick = () => {
    setshowNotification(false);
  };

  return (
    <>
      <>
        <div></div>
        <div className="notification-hero-container">
          <div className="head-notification">
            <div className="head-not-txt">Notifications</div>
            {/* <div onClick={handleSettingsClick}>
                <Settings />
              </div> */}
          </div>
          {/* Single list - no tabs */}
          <div className="mark-all-read-row">
            <span className="mark-all-read-link" onClick={handleMarkAllRead}>Mark all as read</span>
          </div>
          <div className="notify-cnt" onScroll={handleScroll}>
            {renderNotifications()}
            {loading && <ContentLoader />}
          </div>
        </div>

        <div className="notify-mobile">
          <div id="background-in">
            <div id="oval-in-1"></div>
            <div id="oval-in-2"></div>
          </div>
          <header className="termsofuse-header">
            {/* <div className="termsofuse-gradient-background"> */}
            <div className="termsofuse-nav-container">
              <div className="termsofuse-logo-container-notification">
                <img
                  src={arrowLeft}
                  alt="Back"
                  className="termsofuse-back-button"
                  onClick={handleBackClick}
                />
                {/* 
                  <div className="cur-pt" onClick={handleSettingsClick}>
                    <Settings />
                  </div> */}
              </div>
              <h1 className="head-not-txt">Notifications</h1>
              <div className="termsofuse-space"></div>
              <div className="set-phone-modal-support mobile-sub-txt-set-phone"></div>
            </div>
            {/* </div> */}
          </header>
          <div className="not-phone">
            <div className="mark-all-read-row">
              {notifications?.some((n) => !n?.is_read) && (
                <span className="mark-all-read-link" onClick={handleMarkAllRead}>Mark all as read</span>
              )}
            </div>
            <div className="notify-cnt" onScroll={handleScroll}>
              {renderNotifications()}
              {loading && (
                <div className="loader-df-container">
                  <ContentLoader />
                </div>
              )}{" "}
            </div>
          </div>
        </div>

        <InviteDecisionModal
          open={inviteModalOpen}
          invite={inviteNotification}
          onClose={() => setInviteModalOpen(false)}
          onApproved={() => {
            setInviteModalOpen(false);
            // Optionally refresh notifications
            setCurrentPage(1);
            setHasMore(true);
            setNotifications([]);
            fetchNotifications(1);
            if (typeof refreshUnreadCount === 'function') { refreshUnreadCount(); }
          }}
        />
      </>
    </>
  );
}

export default NotificationContainer;
